import React from "react";
import { LazyMotion, domAnimation, m } from "framer-motion";
import { Eye, MonitorPlay, User } from "lucide-react";

import BasicNavbar from "../BasicNavbar";
import BrandsSection from "../BrandsSection";
import FAQSection from "../FAQSection";
import SiteFooter from "../../../components/SiteFooter";

type Feature = {
  title: string;
  description: string;
};

const features: Feature[] = [
  {
    title: "效果展示",
    description: "以“真实消费者视角 + AI导购能力 + 门店系统联动”构建的完整用户故事线",
  },
  {
    title: "角色设定",
    description:
      "用户在线上看到一个零售品牌的新品（如服装、美妆、家居用品）。 TA关注商品是否适合自己、是否能试用/试穿、库存情况、门店是否有货，以及售后退换政策。",
  },
];

export default function SolutionECommercePage() {
  return (
    <div className="dark">
      <div className="bg-background relative flex min-h-screen min-h-dvh w-full flex-col overflow-x-hidden overflow-y-auto scroll-smooth">
        <BasicNavbar />
        <main className="relative z-10 flex w-full flex-1 flex-col items-center">
          <section
            id="solution-e-commerce"
            className="scroll-mt-[calc(80px+var(--intl-notice-height,0px))] mx-auto w-full max-w-6xl px-6 pt-16 pb-0 sm:px-8 sm:pt-20"
          >
            <LazyMotion features={domAnimation}>
              <div className="flex flex-col items-center">
                <m.div
                  initial={{ opacity: 0, y: 14 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{
                    duration: 0.55,
                    ease: [0.22, 1, 0.36, 1] as const,
                  }}
                  className="w-full flex flex-col items-center mt-6"
                >
                  <div className="inline-flex items-center gap-2 rounded-full border border-default-100/60 bg-default-100/10 px-4 py-1.5 text-xs font-medium text-default-500 backdrop-blur">
                    <MonitorPlay className="h-3.5 w-3.5 text-primary" />
                    应用演示 Demo
                  </div>

                  <h2 className="mt-4 text-center text-2xl font-bold tracking-tight text-foreground sm:text-4xl">
                    零售与消费解决方案
                  </h2>
                  <p className="mt-4 text-center text-default-400 leading-7">
                    服饰/美妆/家居/连锁零售
                  </p>
                </m.div>

                <m.div
                  initial={{ opacity: 0, y: 18 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{
                    duration: 0.6,
                    ease: [0.22, 1, 0.36, 1] as const,
                    delay: 0.06,
                  }}
                  className="mt-10 w-full"
                >
                  {/* Video area placeholder (intentionally empty) */}
                  <div className="relative overflow-hidden rounded-3xl border border-default-100/60 bg-default-100/10">
                    <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-primary/20 via-secondary/20 to-emerald-500/10 opacity-80" />
                    <div
                      style={{
                        position: "relative",
                        paddingBottom: "calc(56.25%)",
                        height: 0,
                        width: "100%",
                      }}
                    >
                      <iframe
                        src="https://demo.arcade.software/pTP88ladWAgl4jlsXh0K?embed&embed_mobile=tab&embed_desktop=inline&show_copy_link=true"
                        title="🧑🏻‍💻 零售与消费-应用演示Demo ✅"
                        frameBorder="0"
                        loading="lazy"
                        allowFullScreen
                        allow="clipboard-write"
                        style={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          width: "100%",
                          height: "100%",
                          colorScheme: "light",
                        }}
                      />
                    </div>
                  </div>
                </m.div>

                <m.div
                  initial={{ opacity: 0, y: 22 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{
                    duration: 0.65,
                    ease: [0.22, 1, 0.36, 1] as const,
                    delay: 0.1,
                  }}
                  className="mt-10 grid w-full grid-cols-1 gap-4 sm:grid-cols-2"
                >
                  {features.map((feature, idx) => (
                    <div
                      // eslint-disable-next-line react/no-array-index-key
                      key={`${feature.title}-${idx}`}
                      className="rounded-2xl border border-default-100/60 bg-default-100/20 p-5"
                    >
                      <h3 className="flex items-center gap-2 text-foreground font-semibold">
                        {feature.title === "效果展示" ? (
                          <Eye className="h-5 w-5 text-primary" />
                        ) : (
                          <User className="h-5 w-5 text-primary" />
                        )}
                        {feature.title}
                      </h3>
                      <p className="text-default-500 mt-2 leading-6 text-sm">
                        {feature.description}
                      </p>
                    </div>
                  ))}
                </m.div>
              </div>
            </LazyMotion>
          </section>
        </main>
        <BrandsSection category="零售与消费" />
        <FAQSection />
        <SiteFooter />
      </div>
    </div>
  );
}

